/**
 * FormActionBar — the universal wrapper for "primary CTA at bottom of a form".
 *
 * Before this existed every screen hand-rolled its own bottom-action
 * container: a plain `<View>`, sometimes `position:'absolute'`, with
 * `paddingHorizontal: SPACING.lg` that on desktop let the button stretch
 * edge-to-edge under a centered 800px form. The 1408-wide-button-under-
 * 800px-form mismatch reads as "unfinished".
 *
 * This primitive:
 *   - On desktop: caps children at `COLUMN_PRESETS.content` (800), centers,
 *     pads top/bottom with `SPACING.md`.
 *   - On mobile: full-width row with safe-area-aware bottom padding.
 *
 * Drop-in replacement for the hand-rolled `<View>` wrappers. Pass the
 * `Button` (or whatever you're showing) as `children`.
 */
import React from 'react'
import { View, type ViewStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { SPACING } from '../../lib/brand'
import { COLUMN_PRESETS } from '../../lib/theme/desktop-spacing'

interface FormActionBarProps {
  children: React.ReactNode
  /** Extra style overrides (rare — background color for sticky bars, etc). */
  style?: ViewStyle
  /** E2E selector for the action-bar region. */
  testID?: string
}

export function FormActionBar({ children, style, testID }: FormActionBarProps) {
  const { isDesktop } = useBreakpoint()
  const insets = useSafeAreaInsets()

  return (
    <View
      testID={testID}
      style={[
        {
          paddingHorizontal: isDesktop ? 0 : SPACING.lg,
          paddingTop: SPACING.md,
          paddingBottom: isDesktop ? SPACING.md : insets.bottom + SPACING.md,
          maxWidth: isDesktop ? COLUMN_PRESETS.content : undefined,
          width: '100%',
          alignSelf: 'center',
        },
        style,
      ]}
    >
      {children}
    </View>
  )
}
